/**
 * Family Hub page — sections of key/value facts, the emergency card, caregiver sharing,
 * members, activity, and real-time updates.
 *
 * The hub renders one card per section, and each entry is a row inside it, so most of the DOM work
 * here is "find the card for a section, put the row in the right one, then fix up that card's
 * summary line". Entries move between cards when their section changes.
 *
 * Depends on window.__workspaceData being set by the Razor view.
 */
import { csrfHeaders } from './csrf';
import { toast } from './toast';
import { writeClipboardText } from './clipboard';
import { trackEvent } from './ga-events';
import * as signalR from '@microsoft/signalr';

function initWorkspacePage() {
  'use strict';

  var data = window.__workspaceData;
  if (!data) return;

  var workspaceId = data.workspaceId;
  var currentUserId = data.currentUserId;
  var isOwnerOrAdmin = data.isOwnerOrAdmin;
  var canEdit = data.canEdit;
  var wsLabel = data.displayLabel || 'Team Wiki';
  var guestLabel = data.guestLabel || 'Guest View';
  var shareHost = data.shareHost || '';
  var categories = data.categories || [];

  // Values longer than this stack the label above the value instead of sitting beside it.
  // Kept in sync with StackThreshold in Index.cshtml.
  var STACK_THRESHOLD = 44;

  function findCategory(name) {
    if (!name) return null;
    var lowered = String(name).toLowerCase();
    return categories.find(function (c) { return c.name.toLowerCase() === lowered; }) || null;
  }

  function getCategoryColor(name) {
    var found = findCategory(name);
    return found ? found.color : 'gray';
  }

  /**
   * Escapes a value for interpolation into an HTML string — including into a quoted attribute.
   *
   * The quote escaping is load-bearing, not belt-and-braces. The obvious implementation (append a
   * text node, read innerHTML) escapes &, < and > but NOT quotes, and almost every use here lands
   * inside a double-quoted attribute carrying user input — an entry title, a section name, an
   * emergency label. A title of `" onfocus="…` closes the attribute and injects an event handler.
   * Nothing upstream prevents it: the server trims and length-checks these fields but does not
   * strip quotes.
   */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Attribute selectors break on names containing quotes, and section names are user text.
  // Comparing the parsed dataset value sidesteps escaping entirely.
  function findCardByCategory(name) {
    if (!name) return null;
    var lowered = String(name).toLowerCase();
    var match = null;
    document.querySelectorAll('.hub-card').forEach(function (card) {
      if (!match && (card.dataset.category || '').toLowerCase() === lowered) match = card;
    });
    return match;
  }

  function findRowByEntryId(entryId) {
    if (!entryId) return null;
    var match = null;
    document.querySelectorAll('.hub-row').forEach(function (row) {
      if (!match && row.dataset.entryId === entryId) match = row;
    });
    return match;
  }

  // ── Create workspace ──
  var createForm = document.getElementById('ws-create-form');
  if (createForm) {
    createForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var nameInput = document.getElementById('ws-create-name');
      var name = nameInput ? nameInput.value.trim() : '';
      if (!name) return;

      var btn = document.getElementById('ws-create-btn');
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      fetch('/api/workspace', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ name: name })
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            toast.success(wsLabel + ' created!');
            trackEvent('workspace_created', {});
            window.location.reload();
          } else {
            toast.error(r.data.error || 'Could not create ' + wsLabel.toLowerCase() + '.');
          }
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        })
        .catch(function () {
          toast.error('Network error. Please try again.');
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        });
    });
  }

  if (!workspaceId) return;

  // ── Modal helpers ──
  var previousFocusEl = null;
  var confirmPreviousFocusEl = null;

  function openModal(id) {
    var dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    previousFocusEl = document.activeElement;
    dialog.classList.remove('ws-overlay--closing');
    dialog.showModal();
    document.body.classList.add('ws-body-locked');
    setTimeout(function () {
      // Desktop nicety only. On touch, focusing an input pops the keyboard over half
      // the sheet before the user has read a single field — let them tap when ready.
      if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
      var firstInput = dialog.querySelector('input:not([type="hidden"]), select, textarea, button:not(.ws-modal-close)');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  function closeModal(id, skipAnimation) {
    var dialog = document.getElementById(id);
    if (!dialog || !dialog.open) return;
    var msg = dialog.querySelector('.ws-modal-message');
    if (msg) { msg.hidden = true; msg.textContent = ''; }

    if (skipAnimation) {
      finishClose(dialog);
      return;
    }
    dialog.classList.add('ws-overlay--closing');
    var done = false;
    function onEnd() {
      if (done) return;
      done = true;
      finishClose(dialog);
    }
    var modal = dialog.querySelector('.ws-modal, .ws-confirm');
    if (modal) modal.addEventListener('animationend', onEnd, { once: true });
    setTimeout(onEnd, 250);
  }

  function finishClose(dialog) {
    dialog.classList.remove('ws-overlay--closing');
    dialog.close();
    var anyOpen = document.querySelector('.ws-dialog[open]');
    if (!anyOpen) document.body.classList.remove('ws-body-locked');
    if (previousFocusEl && typeof previousFocusEl.focus === 'function') {
      previousFocusEl.focus();
      previousFocusEl = null;
    }
  }

  function showModalMessage(id, text, isError) {
    var msg = document.getElementById(id);
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = false;
    msg.classList.remove('error', 'success');
    msg.classList.add(isError ? 'error' : 'success');

    // On a phone sheet the body scrolls and the footer is pinned over it, so a
    // message added near the bottom can appear underneath the action bar — the user
    // taps Save again and nothing seems to happen. Bring it into view.
    if (typeof msg.scrollIntoView === 'function') {
      msg.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function updateMemberCount(delta) {
    var tabCount = document.querySelector('#ws-tab-members .ws-tab-count');
    var count = 0;
    if (tabCount) {
      count = Math.max(0, (parseInt(tabCount.textContent, 10) || 0) + delta);
      tabCount.textContent = String(count);
    }

    var capacityBadge = document.getElementById('ws-members-capacity');
    if (capacityBadge) {
      capacityBadge.textContent = count + ' / ' + data.maxMembers + ' seats used';
    }
  }

  function animateCardRemoval(card, onDone) {
    if (!card) { if (onDone) onDone(); return; }
    card.classList.add('ws-card--removing');
    var removed = false;
    function finish() {
      if (removed) return;
      removed = true;
      card.remove();
      if (onDone) onDone();
    }
    card.addEventListener('animationend', finish);
    setTimeout(finish, 350);
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('is-loading', busy);
  }

  // ── Confirmation dialog (replaces browser confirm()) ──
  var confirmResolve = null;
  var confirmDialog = document.getElementById('ws-confirm-overlay');
  var confirmActionBtn = document.getElementById('ws-confirm-action');
  var confirmCancelBtn = document.getElementById('ws-confirm-cancel');

  function confirmAction(opts) {
    return new Promise(function (resolve) {
      confirmResolve = resolve;
      confirmPreviousFocusEl = document.activeElement;
      document.getElementById('ws-confirm-title').textContent = opts.title || 'Are you sure?';
      document.getElementById('ws-confirm-desc').textContent = opts.description || '';
      confirmActionBtn.textContent = opts.confirmText || 'Confirm';
      confirmActionBtn.className = opts.destructive !== false ? 'btn btn-danger' : 'btn btn-primary';
      var iconEl = document.getElementById('ws-confirm-icon');
      iconEl.className = opts.destructive !== false ? 'ws-confirm-icon ws-confirm-icon--destructive' : 'ws-confirm-icon';
      confirmDialog.classList.remove('ws-overlay--closing');
      confirmDialog.showModal();
      document.body.classList.add('ws-body-locked');
      setTimeout(function () { confirmActionBtn.focus(); }, 100);
    });
  }

  function resolveConfirm(result) {
    if (confirmResolve) { var fn = confirmResolve; confirmResolve = null; fn(result); }
    if (!confirmDialog || !confirmDialog.open) return;
    confirmDialog.classList.add('ws-overlay--closing');
    var done = false;
    function onEnd() {
      if (done) return;
      done = true;
      confirmDialog.classList.remove('ws-overlay--closing');
      confirmDialog.close();
      var anyOpen = document.querySelector('.ws-dialog[open]');
      if (!anyOpen) document.body.classList.remove('ws-body-locked');
      if (confirmPreviousFocusEl && typeof confirmPreviousFocusEl.focus === 'function' && document.body.contains(confirmPreviousFocusEl)) {
        confirmPreviousFocusEl.focus();
      }
      confirmPreviousFocusEl = null;
    }
    var inner = confirmDialog.querySelector('.ws-confirm');
    if (inner) inner.addEventListener('animationend', onEnd, { once: true });
    setTimeout(onEnd, 200);
  }

  if (confirmActionBtn) confirmActionBtn.addEventListener('click', function () { resolveConfirm(true); });
  if (confirmCancelBtn) confirmCancelBtn.addEventListener('click', function () { resolveConfirm(false); });
  if (confirmDialog) {
    confirmDialog.addEventListener('click', function (e) { if (e.target === confirmDialog) resolveConfirm(false); });
    confirmDialog.addEventListener('cancel', function (e) { e.preventDefault(); resolveConfirm(false); });
  }

  // ── Rename ──
  var renameBtn = document.getElementById('ws-btn-rename');
  if (renameBtn) {
    renameBtn.addEventListener('click', function () { openModal('ws-rename-overlay'); });
  }
  var renameClose = document.getElementById('ws-rename-close');
  var renameCancel = document.getElementById('ws-rename-cancel');
  if (renameClose) renameClose.addEventListener('click', function () { closeModal('ws-rename-overlay'); });
  if (renameCancel) renameCancel.addEventListener('click', function () { closeModal('ws-rename-overlay'); });

  var renameForm = document.getElementById('ws-rename-form');
  if (renameForm) {
    renameForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = document.getElementById('ws-rename-input');
      var name = input ? input.value.trim() : '';
      if (!name) return;

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/name', {
        method: 'PUT',
        headers: csrfHeaders(),
        body: JSON.stringify({ name: name })
      })
        .then(function (res) {
          if (res.ok) {
            toast.success(wsLabel + ' renamed.');
            trackEvent('workspace_renamed', {});
            var title = document.querySelector('.ws-title');
            if (title) title.textContent = name;
            closeModal('ws-rename-overlay');
          } else {
            return res.json().then(function (d) { showModalMessage('ws-rename-message', d.error || 'Could not rename.', true); });
          }
        })
        .catch(function () { showModalMessage('ws-rename-message', 'Network error.', true); });
    });
  }

  // ── Invite ──
  var inviteBtn = document.getElementById('ws-btn-invite');
  if (inviteBtn) {
    inviteBtn.addEventListener('click', function () { openModal('ws-invite-overlay'); });
  }
  var inviteClose = document.getElementById('ws-invite-close');
  var inviteCancel = document.getElementById('ws-invite-cancel');
  if (inviteClose) inviteClose.addEventListener('click', function () { closeModal('ws-invite-overlay'); });
  if (inviteCancel) inviteCancel.addEventListener('click', function () { closeModal('ws-invite-overlay'); });

  var inviteForm = document.getElementById('ws-invite-form');
  if (inviteForm) {
    inviteForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var emailInput = document.getElementById('ws-invite-email');
      var roleSelect = document.getElementById('ws-invite-role');
      var email = emailInput ? emailInput.value.trim() : '';
      var role = roleSelect ? roleSelect.value : 'Member';
      if (!email) return;

      var btn = document.getElementById('ws-invite-submit');
      setBusy(btn, true);

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/invitations', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ email: email, role: role })
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, status: res.status, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            toast.success('Invitation sent to ' + email);
            trackEvent('workspace_member_invited', { role: role });
            if (emailInput) emailInput.value = '';
            closeModal('ws-invite-overlay');
            insertPendingInvitationCard(r.data);
          } else {
            showModalMessage('ws-invite-message', r.data.error || r.data.message || 'Could not send invitation.', true);
          }
          setBusy(btn, false);
        })
        .catch(function () {
          showModalMessage('ws-invite-message', 'Network error.', true);
          setBusy(btn, false);
        });
    });
  }

  // ── Revoke invitation ──
  document.querySelectorAll('.ws-btn-revoke').forEach(function (btn) {
    wireRevokeButton(btn);
  });

  // ── Role change ──
  document.querySelectorAll('.ws-role-select').forEach(function (select) {
    select.addEventListener('change', function () {
      var targetUserId = select.dataset.userId;
      var newRole = select.value;

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/members/' + encodeURIComponent(targetUserId) + '/role', {
        method: 'PUT',
        headers: csrfHeaders(),
        body: JSON.stringify({ role: newRole })
      })
        .then(function (res) {
          if (res.ok) {
            toast.success('Role updated.');
            trackEvent('workspace_role_changed', { role: newRole });
          } else {
            return res.json().then(function (d) { toast.error(d.error || 'Could not change role.'); });
          }
        })
        .catch(function () { toast.error('Network error.'); });
    });
  });

  // ── Remove member ──
  document.querySelectorAll('.ws-btn-remove').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var targetUserId = btn.dataset.userId;
      if (!targetUserId) return;

      confirmAction({
        title: 'Remove this member?',
        description: 'They will lose access to the ' + wsLabel.toLowerCase() + ' and will need a new invitation to rejoin.',
        confirmText: 'Remove',
        destructive: true
      }).then(function (confirmed) {
        if (!confirmed) return;

        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/members/' + encodeURIComponent(targetUserId), {
          method: 'DELETE',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              toast.success('Member removed.');
              trackEvent('workspace_member_removed', {});
              var card = btn.closest('.ws-side-member') || btn.closest('.ws-member-tile') || btn.closest('.ws-member-card');
              animateCardRemoval(card, function () { updateMemberCount(-1); });
            } else {
              toast.error('Could not remove member.');
            }
          })
          .catch(function () { toast.error('Network error.'); });
      });
    });
  });

  // ── Leave workspace ──
  document.querySelectorAll('.ws-btn-leave').forEach(function (btn) {
    btn.addEventListener('click', function () {
      confirmAction({
        title: 'Leave this ' + wsLabel.toLowerCase() + '?',
        description: 'You will lose access and need a new invitation to rejoin.',
        confirmText: 'Leave',
        destructive: true
      }).then(function (confirmed) {
        if (!confirmed) return;

        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/leave', {
          method: 'POST',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              toast.success('You left the ' + wsLabel.toLowerCase() + '.');
              trackEvent('workspace_left', {});
              window.location.reload();
            } else {
              return res.json().then(function (d) { toast.error(d.error || 'Could not leave ' + wsLabel.toLowerCase() + '.'); });
            }
          })
          .catch(function () { toast.error('Network error.'); });
      });
    });
  });

  // ── Dynamic member helpers ──
  function getAvatarHue(identifier) {
    if (!identifier) return 220;
    var hash = 0;
    for (var i = 0; i < identifier.length; i++) hash = (hash * 31 + identifier.charCodeAt(i)) | 0;
    return Math.abs(hash) % 360;
  }

  function getInitials(name) {
    if (!name) return '?';
    var parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  function insertPendingInvitationCard(inv) {
    var panel = document.getElementById('ws-panel-members');
    if (!panel) return;
    var pendingSection = panel.querySelector('.ws-pending-section');
    if (!pendingSection) {
      pendingSection = document.createElement('div');
      pendingSection.className = 'ws-pending-section';
      pendingSection.innerHTML = '<h3 class="ws-section-title">Pending</h3><div class="ws-member-list"></div>';
      var heading = panel.querySelector('.ws-panel-heading');
      if (heading && heading.nextSibling) {
        panel.insertBefore(pendingSection, heading.nextSibling);
      } else if (heading) {
        panel.appendChild(pendingSection);
      } else {
        panel.insertBefore(pendingSection, panel.firstChild);
      }
    }
    var list = pendingSection.querySelector('.ws-member-list');
    if (!list) return;

    var expiresDate = inv.expiresUtc ? new Date(inv.expiresUtc).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    var card = document.createElement('div');
    card.className = 'ws-member-card ws-member-card--pending ws-member-card--entering';
    card.dataset.invitationId = inv.invitationId;
    card.innerHTML =
      '<div class="ws-member-avatar ws-member-avatar--pending" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></div>' +
      '<div class="ws-member-info"><span class="ws-member-name">' + escapeHtml(inv.invitedEmail) + '</span>' +
      '<span class="ws-member-meta">Invited as ' + escapeHtml(inv.invitedRole) + (expiresDate ? ' · Expires ' + expiresDate : '') + '</span></div>' +
      '<button type="button" class="btn btn-sm btn-outline-danger ws-btn-revoke" data-invitation-id="' + escapeHtml(inv.invitationId) + '" aria-label="Revoke invitation for ' + escapeHtml(inv.invitedEmail) + '">Revoke</button>';

    var revokeBtn = card.querySelector('.ws-btn-revoke');
    if (revokeBtn) wireRevokeButton(revokeBtn);

    list.appendChild(card);
    card.addEventListener('animationend', function () { card.classList.remove('ws-member-card--entering'); }, { once: true });
  }

  function buildMemberCard(payload) {
    var hue = getAvatarHue(payload.userId || payload.email);
    var initials = getInitials(payload.displayName || payload.email);
    var card = document.createElement('div');
    card.className = 'ws-side-member ws-member-card--entering';
    card.dataset.userId = payload.userId || '';
    card.innerHTML =
      '<div class="ws-member-avatar" style="--avatar-hue: ' + hue + '" aria-hidden="true">' + escapeHtml(initials) + '</div>' +
      '<div class="ws-side-member-info">' +
        '<span class="ws-side-member-name">' + escapeHtml(payload.displayName || payload.email || 'Unknown') + '</span>' +
        '<span class="ws-member-role">' + escapeHtml(payload.role || 'Member') + '</span>' +
      '</div>';
    card.addEventListener('animationend', function () { card.classList.remove('ws-member-card--entering'); }, { once: true });
    return card;
  }

  function wireRevokeButton(btn) {
    btn.addEventListener('click', function () {
      var invitationId = btn.dataset.invitationId;
      if (!invitationId) return;
      confirmAction({
        title: 'Revoke this invitation?',
        description: 'The invited person will no longer be able to join this ' + wsLabel.toLowerCase() + '.',
        confirmText: 'Revoke',
        destructive: true
      }).then(function (confirmed) {
        if (!confirmed) return;
        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/invitations/' + encodeURIComponent(invitationId), {
          method: 'DELETE',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              toast.success('Invitation revoked.');
              var card = btn.closest('.ws-member-card');
              animateCardRemoval(card, function () {
                var pendingSection = document.querySelector('.ws-pending-section');
                if (pendingSection && !pendingSection.querySelector('.ws-member-card')) {
                  pendingSection.remove();
                }
              });
            } else {
              toast.error('Could not revoke invitation.');
            }
          })
          .catch(function () { toast.error('Network error.'); });
      });
    });
  }

  // ── Hub search ──
  // Searches sections and rows together. A section whose *name* matches stays fully visible, so
  // typing "school" shows everything filed under School rather than only rows repeating the word.
  var currentSearchTerm = '';
  var searchInput = document.getElementById('hub-search-input');

  function filterHub() {
    var term = currentSearchTerm.toLowerCase();
    var anyVisible = false;

    document.querySelectorAll('.hub-card').forEach(function (card) {
      var sectionMatches = !term || (card.dataset.category || '').toLowerCase().indexOf(term) !== -1;
      var visibleRows = 0;

      card.querySelectorAll('.hub-row').forEach(function (row) {
        var key = (row.querySelector('[data-role="row-key"]') || {}).textContent || '';
        var val = (row.querySelector('[data-role="row-val"]') || {}).textContent || '';
        var match = !term || sectionMatches ||
          key.toLowerCase().indexOf(term) !== -1 ||
          val.toLowerCase().indexOf(term) !== -1;
        row.hidden = !match;
        if (match) visibleRows++;
      });

      // While searching, an empty section is noise. With no search term it's a prompt to fill it in.
      var show = !term ? true : (sectionMatches || visibleRows > 0);
      card.hidden = !show;
      if (show) anyVisible = true;

      // The "Nothing here yet" line would be misleading next to rows hidden by a search.
      var blank = card.querySelector('[data-role="card-blank"]');
      if (blank) {
        var totalRows = card.querySelectorAll('.hub-row').length;
        blank.style.display = (!term && totalRows === 0) ? '' : 'none';
      }
    });

    var emergency = document.getElementById('hub-emergency');
    if (emergency) {
      var emergencyMatches = !term || (emergency.textContent || '').toLowerCase().indexOf(term) !== -1;
      emergency.hidden = !emergencyMatches;
      if (emergencyMatches) anyVisible = true;
    }

    var noResults = document.getElementById('hub-no-results');
    if (noResults) noResults.style.display = (term && !anyVisible) ? '' : 'none';
  }

  if (searchInput) {
    searchInput.addEventListener('input', function () {
      currentSearchTerm = searchInput.value.trim();
      filterHub();
    });
  }

  // '/' focuses search, as long as the user isn't already typing somewhere.
  document.addEventListener('keydown', function (e) {
    if (e.key === '/' && !e.target.matches('input, textarea, select, [contenteditable]')) {
      if (searchInput) {
        e.preventDefault();
        searchInput.focus();
        searchInput.select();
      }
    }
  });

  // ── Section card upkeep ──
  function relativeTime(iso) {
    var then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    var mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    if (days < 7) return days + 'd ago';
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function refreshCardState(card) {
    if (!card) return;
    var rows = card.querySelectorAll('.hub-row');

    // The strays card exists only to surface entries with no section. Once the last one has been
    // filed it has nothing to say, so it goes rather than sitting there empty.
    if (card.classList.contains('hub-card--orphans')) {
      if (rows.length === 0) card.remove();
      return;
    }

    var blank = card.querySelector('[data-role="card-blank"]');
    if (blank) blank.style.display = rows.length === 0 ? '' : 'none';

    if (window.familyHubSyncNudge) window.familyHubSyncNudge();

    var meta = card.querySelector('[data-role="card-meta"]');
    if (!meta) return;

    if (rows.length === 0) {
      meta.textContent = 'Nothing here yet';
      return;
    }

    var newest = null;
    var newestAuthor = '';
    rows.forEach(function (row) {
      var stamp = new Date(row.dataset.date || 0).getTime();
      if (!isNaN(stamp) && (newest === null || stamp > newest)) {
        newest = stamp;
        newestAuthor = row.dataset.author || '';
      }
    });

    if (newest === null) { meta.textContent = ''; return; }
    meta.textContent = (newestAuthor ? 'Updated by ' + newestAuthor : 'Updated') +
      ' · ' + relativeTime(new Date(newest).toISOString());
  }

  function refreshAllCards() {
    document.querySelectorAll('.hub-card').forEach(refreshCardState);
  }

  function buildRow(entry) {
    var row = document.createElement('div');
    row.className = 'hub-row hub-row--entering';
    if ((entry.content || '').length > STACK_THRESHOLD) row.classList.add('hub-row--stacked');
    row.dataset.entryId = entry.entryId;
    row.dataset.category = entry.category;
    row.dataset.author = entry.createdByDisplayName || '';
    row.dataset.date = entry.updatedUtc || entry.createdUtc || new Date().toISOString();
    row.tabIndex = 0;
    row.setAttribute('role', 'button');
    row.setAttribute('aria-label', 'View ' + (entry.title || ''));

    var html =
      '<span class="hub-row-key" data-role="row-key">' + escapeHtml(entry.title) + '</span>' +
      '<span class="hub-row-val" data-role="row-val">' + escapeHtml(entry.content) + '</span>';

    if (canEdit) {
      html += '<span class="hub-row-actions">' +
        '<button type="button" class="hub-row-btn" data-action="edit" data-entry-id="' + escapeHtml(entry.entryId) + '" aria-label="Edit ' + escapeHtml(entry.title) + '" title="Edit"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>' +
        '<button type="button" class="hub-row-btn hub-row-btn--danger" data-action="archive" data-entry-id="' + escapeHtml(entry.entryId) + '" aria-label="Archive ' + escapeHtml(entry.title) + '" title="Archive"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>' +
        '</span>';
    }

    row.innerHTML = html;
    row.addEventListener('animationend', function () { row.classList.remove('hub-row--entering'); }, { once: true });
    return row;
  }

  // Rows sit in alphabetical order by label, matching what the server rendered — otherwise a new
  // entry lands at the bottom on this page load and somewhere else after a refresh.
  function insertRowSorted(card, row) {
    var container = card.querySelector('[data-role="rows"]');
    if (!container) return;
    var key = ((row.querySelector('[data-role="row-key"]') || {}).textContent || '').toLowerCase();
    var placed = false;

    Array.prototype.slice.call(container.querySelectorAll('.hub-row')).some(function (existing) {
      var existingKey = ((existing.querySelector('[data-role="row-key"]') || {}).textContent || '').toLowerCase();
      if (existingKey > key) {
        container.insertBefore(row, existing);
        placed = true;
        return true;
      }
      return false;
    });

    if (!placed) container.appendChild(row);
  }

  // ── Entry detail ──
  var detailOverlayId = 'ws-detail-overlay';
  var currentDetailEntryId = null;

  function rowFields(row) {
    return {
      title: (row.querySelector('[data-role="row-key"]') || {}).textContent || '',
      content: (row.querySelector('[data-role="row-val"]') || {}).textContent || '',
      category: row.dataset.category || '',
      author: row.dataset.author || '',
      date: row.dataset.date || ''
    };
  }

  function openDetailView(row) {
    if (!row) return;
    currentDetailEntryId = row.dataset.entryId;
    var f = rowFields(row);

    var categoryEl = document.getElementById('ws-detail-category');
    if (categoryEl) {
      categoryEl.textContent = f.category;
      categoryEl.setAttribute('data-color', getCategoryColor(f.category));
    }
    var titleEl = document.getElementById('ws-detail-title');
    if (titleEl) titleEl.textContent = f.title;
    var contentEl = document.getElementById('ws-detail-content');
    if (contentEl) contentEl.textContent = f.content;
    var authorSpan = document.querySelector('#ws-detail-author span');
    if (authorSpan) authorSpan.textContent = f.author || 'Team member';
    var dateSpan = document.querySelector('#ws-detail-date span');
    if (dateSpan) {
      var d = new Date(f.date);
      dateSpan.textContent = isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    openModal(detailOverlayId);
  }

  var detailClose = document.getElementById('ws-detail-close');
  if (detailClose) detailClose.addEventListener('click', function () { closeModal(detailOverlayId); });

  var detailCopyBtn = document.getElementById('ws-detail-copy');
  if (detailCopyBtn) {
    detailCopyBtn.addEventListener('click', function () {
      var contentEl = document.getElementById('ws-detail-content');
      var text = contentEl ? contentEl.textContent : '';
      if (!text) return;
      copyText(text).then(function (ok) {
        if (ok) toast.success('Copied.');
        else toast.error('Could not copy.');
      });
    });
  }

  var detailEditBtn = document.getElementById('ws-detail-edit');
  if (detailEditBtn) {
    detailEditBtn.addEventListener('click', function () {
      var row = findRowByEntryId(currentDetailEntryId);
      if (!row) return;
      closeModal(detailOverlayId);
      openEntryModal(row);
    });
  }

  var detailArchiveBtn = document.getElementById('ws-detail-archive');
  if (detailArchiveBtn) {
    detailArchiveBtn.addEventListener('click', function () {
      if (!currentDetailEntryId) return;
      var id = currentDetailEntryId;
      closeModal(detailOverlayId);
      archiveEntry(id);
    });
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(function () { return true; }).catch(function () { return false; });
    }
    // execCommand is deprecated but is the only fallback on plain-http origins, which is exactly
    // where local development runs.
    return new Promise(function (resolve) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      document.body.removeChild(ta);
      resolve(ok);
    });
  }

  // ── Entry CRUD ──
  var wikiOverlayId = 'ws-wiki-overlay';

  function openEntryModal(row, presetCategory) {
    var idField = document.getElementById('ws-wiki-entry-id');
    var titleField = document.getElementById('ws-wiki-title-input');
    var contentField = document.getElementById('ws-wiki-content-input');
    var categoryField = document.getElementById('ws-wiki-category-select');
    var counter = document.getElementById('ws-wiki-char-count');
    var heading = document.getElementById('ws-wiki-modal-title');
    if (!idField || !titleField || !contentField || !categoryField) return;

    // Drop any stray option left by a previous open, or they accumulate one per visit.
    categoryField.querySelectorAll('option[data-stray]').forEach(function (o) { o.remove(); });

    if (row) {
      var f = rowFields(row);
      idField.value = row.dataset.entryId;
      titleField.value = f.title;
      contentField.value = f.content;

      // An entry from the strays card names a section that no longer exists. Offer it as an option
      // so opening the editor doesn't quietly refile the entry under whatever sorts first.
      if (f.category && !findCategory(f.category)) {
        var stray = document.createElement('option');
        stray.value = f.category;
        stray.textContent = f.category + ' (missing section)';
        stray.dataset.stray = 'true';
        categoryField.insertBefore(stray, categoryField.firstChild);
      }

      categoryField.value = f.category;
      if (counter) counter.textContent = String(f.content.length);
      if (heading) heading.textContent = 'Edit entry';
    } else {
      idField.value = '';
      titleField.value = '';
      contentField.value = '';
      categoryField.value = presetCategory || (categories.length > 0 ? categories[0].name : '');
      if (counter) counter.textContent = '0';
      if (heading) heading.textContent = 'Add entry';
    }

    openModal(wikiOverlayId);
  }

  function archiveEntry(entryId) {
    if (!entryId) return;
    confirmAction({
      title: 'Remove this entry?',
      description: 'It will be archived and disappear from the hub. Nothing else changes.',
      confirmText: 'Remove',
      destructive: true
    }).then(function (confirmed) {
      if (!confirmed) return;

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/intelligence/' + encodeURIComponent(entryId), {
        method: 'DELETE',
        headers: csrfHeaders()
      })
        .then(function (res) {
          if (res.ok) {
            toast.success('Entry removed.');
            trackEvent('workspace_wiki_archived', {});
            removeRow(entryId);
          } else {
            toast.error('Could not remove entry.');
          }
        })
        .catch(function () { toast.error('Network error.'); });
    });
  }

  function removeRow(entryId) {
    var row = findRowByEntryId(entryId);
    if (!row) return;
    var card = row.closest('.hub-card');
    row.classList.add('hub-row--removing');
    var removed = false;
    function doRemove() {
      if (removed) return;
      removed = true;
      row.remove();
      refreshCardState(card);
      filterHub();
    }
    row.addEventListener('animationend', doRemove);
    // Fallback for prefers-reduced-motion, where the animation never fires.
    setTimeout(doRemove, 350);
  }

  // Rows are wired through one delegated listener on the grid rather than per-row handlers, so
  // rows added later need no wiring at all.
  var hubGrid = document.getElementById('hub-grid');
  if (hubGrid) {
    hubGrid.addEventListener('click', function (e) {
      var actionBtn = e.target.closest('[data-action]');
      if (actionBtn && hubGrid.contains(actionBtn)) {
        var action = actionBtn.dataset.action;

        if (action === 'add-entry') {
          openEntryModal(null, actionBtn.dataset.category);
          return;
        }
        if (action === 'edit-category') {
          openCategoryEditor(actionBtn.dataset.category);
          return;
        }
        if (action === 'edit') {
          e.stopPropagation();
          openEntryModal(actionBtn.closest('.hub-row'));
          return;
        }
        if (action === 'archive') {
          e.stopPropagation();
          archiveEntry(actionBtn.dataset.entryId);
          return;
        }
      }

      var row = e.target.closest('.hub-row');
      if (row && hubGrid.contains(row)) openDetailView(row);
    });

    hubGrid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var row = e.target.closest('.hub-row');
      if (!row || e.target.closest('[data-action]')) return;
      e.preventDefault();
      openDetailView(row);
    });
  }

  var wikiClose = document.getElementById('ws-wiki-close');
  var wikiCancel = document.getElementById('ws-wiki-cancel');
  if (wikiClose) wikiClose.addEventListener('click', function () { closeModal(wikiOverlayId); });
  if (wikiCancel) wikiCancel.addEventListener('click', function () { closeModal(wikiOverlayId); });

  var wikiContentInput = document.getElementById('ws-wiki-content-input');
  if (wikiContentInput) {
    wikiContentInput.addEventListener('input', function () {
      var counter = document.getElementById('ws-wiki-char-count');
      if (counter) counter.textContent = String(wikiContentInput.value.length);
    });
  }

  var wikiForm = document.getElementById('ws-wiki-form');
  if (wikiForm) {
    wikiForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var entryId = document.getElementById('ws-wiki-entry-id').value;
      var title = document.getElementById('ws-wiki-title-input').value.trim();
      var content = document.getElementById('ws-wiki-content-input').value.trim();
      var category = document.getElementById('ws-wiki-category-select').value;

      if (!title || !content) {
        showModalMessage('ws-wiki-message', 'A label and a detail are both required.', true);
        return;
      }

      var submitBtn = document.getElementById('ws-wiki-submit');
      setBusy(submitBtn, true);

      var isUpdate = !!entryId;
      var url = '/api/workspace/' + encodeURIComponent(workspaceId) + '/intelligence' + (isUpdate ? '/' + encodeURIComponent(entryId) : '');
      var body = { title: title, content: content, category: category };
      if (!isUpdate) body.scope = 'Shared';

      fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            toast.success(isUpdate ? 'Entry updated.' : 'Entry added.');
            trackEvent(isUpdate ? 'workspace_wiki_updated' : 'workspace_wiki_created', { category: category });
            closeModal(wikiOverlayId);

            var targetCard = findCardByCategory(category);
            if (!targetCard) {
              // The section vanished underneath us (removed in another tab). A reload is the honest
              // way to resync rather than inventing a card the server doesn't know about.
              window.location.reload();
              return;
            }

            // Read the author before the old row goes away — an edit must not reattribute the
            // entry to whoever happened to make it.
            var priorAuthor = '';
            if (isUpdate) {
              var existing = findRowByEntryId(entryId);
              if (existing) {
                priorAuthor = existing.dataset.author || '';
                var previousCard = existing.closest('.hub-card');
                existing.remove();
                if (previousCard && previousCard !== targetCard) refreshCardState(previousCard);
              }
            }

            var payload = {
              entryId: isUpdate ? entryId : (r.data.entryId || r.data.EntryId),
              title: title,
              content: content,
              category: category,
              createdByDisplayName: r.data.createdByDisplayName || priorAuthor || 'You',
              updatedUtc: new Date().toISOString()
            };

            var newRow = buildRow(payload);
            insertRowSorted(targetCard, newRow);
            refreshCardState(targetCard);
            filterHub();
          } else {
            showModalMessage('ws-wiki-message', r.data.error || 'Could not save entry.', true);
          }
          setBusy(submitBtn, false);
        })
        .catch(function () {
          showModalMessage('ws-wiki-message', 'Network error.', true);
          setBusy(submitBtn, false);
        });
    });
  }

  // ── Emergency card ──
  var emergencyOverlayId = 'hub-emergency-overlay';

  function openEmergencyModal(tile, presetKind) {
    var idField = document.getElementById('hub-emergency-item-id');
    if (!idField) return;

    var kind = document.getElementById('hub-emergency-kind');
    var name = document.getElementById('hub-emergency-name');
    var phone = document.getElementById('hub-emergency-phone');
    var address = document.getElementById('hub-emergency-address');
    var note = document.getElementById('hub-emergency-note');
    var label = document.getElementById('hub-emergency-label');
    var heading = document.getElementById('hub-emergency-modal-title');
    var deleteBtn = document.getElementById('hub-emergency-delete');

    if (tile) {
      idField.value = tile.dataset.itemId || '';
      if (kind) kind.value = tile.dataset.kind || 'Custom';
      if (name) name.value = tile.dataset.name || '';
      if (phone) phone.value = tile.dataset.phone || '';
      if (address) address.value = tile.dataset.address || '';
      if (note) note.value = tile.dataset.note || '';
      if (label) label.value = tile.dataset.label || '';
      if (heading) heading.textContent = 'Edit emergency entry';
      if (deleteBtn) deleteBtn.hidden = false;
    } else {
      idField.value = '';
      // presetKind comes from an empty-slot placeholder, so "Add nearest ER" opens on Nearest ER rather
      // than making the parent find it in the dropdown again.
      if (kind) kind.value = presetKind || 'CallFirst';
      if (name) name.value = '';
      // Poison Control is a constant, so its placeholder can fill itself in and leave nothing to type.
      if (phone) phone.value = presetKind === 'PoisonControl' ? '1-800-222-1222' : '';
      if (name && presetKind === 'PoisonControl') name.value = 'Poison Control';
      if (address) address.value = '';
      if (note) note.value = presetKind === 'PoisonControl' ? 'National hotline, 24/7' : '';
      if (label) label.value = '';
      if (heading) heading.textContent = 'Add emergency entry';
      if (deleteBtn) deleteBtn.hidden = true;
    }

    openModal(emergencyOverlayId);
  }

  // Mirrors FamilyHubEmergencyKind.DefaultLabel / DefaultIcon. Only used to rebuild a slot the server
  // did not render because the kind was filled at page load.
  var EMERGENCY_LABELS = {
    CallFirst: 'Call first', MedicalAlert: 'Medical alert', HomeAddress: 'Home address',
    EmergencyRoom: 'Nearest ER', UrgentCare: 'Urgent care', Pediatrician: 'Pediatrician',
    PoisonControl: 'Poison control', Fire: 'Fire (non-emergency)', Police: 'Police (non-emergency)'
  };
  var EMERGENCY_ICONS = {
    CallFirst: '\uD83D\uDCDE', MedicalAlert: '\uD83D\uDEA8', HomeAddress: '\uD83D\uDCCD',
    EmergencyRoom: '\uD83D\uDE91', UrgentCare: '\uD83C\uDFE5', Pediatrician: '\uD83E\uDE7A',
    PoisonControl: '\u2623\uFE0F', Fire: '\uD83D\uDE92', Police: '\uD83D\uDE93'
  };

  function buildEmergencyTile(item) {
    var tile = document.createElement('div');
    tile.className = 'hub-tile hub-tile--entering';
    if (item.kind === 'MedicalAlert') tile.classList.add('hub-tile--alert');
    tile.dataset.itemId = item.itemId;
    tile.dataset.kind = item.kind;
    tile.dataset.label = item.hasCustomLabel ? item.label : '';
    tile.dataset.name = item.name || '';
    tile.dataset.phone = item.phone || '';
    tile.dataset.address = item.address || '';
    tile.dataset.note = item.note || '';
    tile.dataset.sortOrder = String(item.sortOrder || 0);

    // Keep this block in step with the same markup in Views/Workspace/Index.cshtml — a tile added in
    // this session must offer the same actions as one that arrived with the page.
    var copyText = item.address || item.note || item.name || '';
    var html = '<div class="hub-tile-actions">';
    if (item.dialablePhone) {
      html += '<a class="hub-tile-action" href="/api/workspace/' + encodeURIComponent(workspaceId) +
        '/emergency/' + encodeURIComponent(item.itemId) + '/contact.vcf" download aria-label="Save ' +
        escapeHtml(item.label) + ' to contacts" title="Save to contacts"><span aria-hidden="true">📇</span></a>';
    } else if (copyText) {
      html += '<button type="button" class="hub-tile-action" data-action="copy-emergency" data-copy="' +
        escapeHtml(copyText) + '" aria-label="Copy ' + escapeHtml(item.label) + '" title="Copy">' +
        '<span aria-hidden="true">📋</span></button>';
    }
    if (canEdit) {
      html += '<button type="button" class="hub-tile-edit" data-action="edit-emergency" aria-label="Edit ' + escapeHtml(item.label) + '" title="Edit"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>';
    }
    html += '</div>';
    html += '<p class="hub-tile-label"><span aria-hidden="true">' + escapeHtml(item.icon) + '</span><span>' + escapeHtml(item.label) + '</span></p>';
    html += '<p class="hub-tile-value">' + escapeHtml(item.name) + '</p>';
    if (item.phone) {
      html += item.dialablePhone
        ? '<a class="hub-tile-phone" href="tel:' + escapeHtml(item.dialablePhone) + '">' + escapeHtml(item.phone) + '</a>'
        : '<span class="hub-tile-phone">' + escapeHtml(item.phone) + '</span>';
    }
    if (item.address) html += '<span class="hub-tile-address">' + escapeHtml(item.address) + '</span>';
    if (item.note) html += '<p class="hub-tile-note">' + escapeHtml(item.note) + '</p>';

    tile.innerHTML = html;
    tile.addEventListener('animationend', function () { tile.classList.remove('hub-tile--entering'); }, { once: true });
    return tile;
  }

  /**
   * Puts an empty slot back when the last entry of a kind is deleted. Slots are server-rendered for the
   * kinds that were unfilled at page load, so a kind filled at load time has none to fall back to.
   */
  function restoreEmptySlot(grid, kind) {
    if (!grid || !kind || !canEdit) return;
    if (grid.querySelector('.hub-tile[data-kind="' + kind + '"]')) return;

    var template = grid.querySelector('.hub-tile--empty');
    if (!template) return; // Nothing to copy the shape from; the next reload renders it.

    var slot = template.cloneNode(true);
    slot.setAttribute('data-kind', kind);
    slot.setAttribute('aria-label', 'Add ' + (EMERGENCY_LABELS[kind] || kind));
    var label = slot.querySelector('.hub-tile-label');
    if (label) label.innerHTML = '<span aria-hidden="true">' + escapeHtml(EMERGENCY_ICONS[kind] || '') +
      '</span><span>' + escapeHtml(EMERGENCY_LABELS[kind] || kind) + '</span>';
    var value = slot.querySelector('.hub-tile-value');
    if (value) value.textContent = 'Not added yet';
    var hint = slot.querySelector('.hub-tile-hint');
    if (hint) hint.remove();
    grid.appendChild(slot);
  }

  function syncEmergencyEmptyState() {
    var grid = document.getElementById('hub-emergency-grid');
    var empty = document.getElementById('hub-emergency-empty');
    if (!grid || !empty) return;
    // Placeholders are prompts, not entries — counting them makes an empty card look full.
    var count = grid.querySelectorAll('.hub-tile:not(.hub-tile--empty)').length;
    grid.style.display = count === 0 ? 'none' : '';
    empty.style.display = count === 0 ? '' : 'none';

    var disclaimer = document.getElementById('hub-emergency-disclaimer');
    if (disclaimer) disclaimer.style.display = count === 0 ? 'none' : '';

    if (window.familyHubSyncNudge) window.familyHubSyncNudge();
  }

  var emergencySection = document.getElementById('hub-emergency');
  if (emergencySection) {
    emergencySection.addEventListener('click', function (e) {
      var editBtn = e.target.closest('[data-action="edit-emergency"]');
      if (editBtn) {
        openEmergencyModal(editBtn.closest('.hub-tile'));
        return;
      }

      var addSlot = e.target.closest('[data-action="add-emergency"]');
      if (addSlot) {
        openEmergencyModal(null, addSlot.getAttribute('data-kind'));
        return;
      }

      var copyBtn = e.target.closest('[data-action="copy-emergency"]');
      if (copyBtn) {
        var text = copyBtn.getAttribute('data-copy') || '';
        if (!text) return;
        writeClipboardText(text)
          .then(function () { toast.success('Copied to clipboard', { duration: 2000 }); })
          .catch(function () { toast.error('Could not copy to clipboard', { duration: 3500 }); });
      }
    });
  }

  // ── Children ─────────────────────────────────────────────
  //
  // The same roster the Cafeteria writes to, so a child added here can plan lunches immediately. Rows are
  // rebuilt in place rather than by reloading the page, which would throw away an open wiki card and the
  // scroll position along with it.

  var kidOverlayId = 'hub-kid-overlay';

  function kidMetaText(child) {
    var bits = [];
    if (child.school) bits.push(child.school);
    if (child.grade) bits.push('Grade ' + child.grade);
    if (child.teacher) bits.push(child.teacher);
    if (child.bedtime) bits.push('Bed ' + child.bedtime);
    return bits.length ? bits.join(' · ') : 'No details yet';
  }

  function syncKidsEmptyState() {
    var list = document.getElementById('hub-kids-list');
    var empty = document.getElementById('hub-kids-empty');
    if (!list || !empty) return;
    var any = list.querySelectorAll('[data-role="kid-row"]').length > 0;
    list.style.display = any ? '' : 'none';
    empty.style.display = any ? 'none' : '';
  }

  function buildKidRow(child) {
    var li = document.createElement('li');
    li.className = 'hub-kid';
    li.setAttribute('data-role', 'kid-row');
    li.setAttribute('data-child-id', child.childId);
    li.setAttribute('data-kid-color', child.colorKey || 'coral');
    // The edit dialog reopens from these, not from the rendered meta line — that line joins fields with a
    // separator and says "No details yet" when empty, so reading it back would blank what it could not
    // recover. setAttribute escapes, so no value here can break out into markup.
    ['name', 'school', 'grade', 'teacher', 'bedtime'].forEach(function (field) {
      li.setAttribute('data-' + field, child[field] || '');
    });

    var main = document.createElement('div');
    main.className = 'hub-kid-main';

    var avatar = document.createElement('span');
    avatar.className = 'hub-kid-avatar';
    avatar.setAttribute('aria-hidden', 'true');
    // Served alongside the name: charAt(0) would split a surrogate pair, and computing it here as well
    // would be a second implementation of the same rule, free to drift from the one the page rendered.
    avatar.textContent = child.initial || '?';

    var text = document.createElement('div');
    text.className = 'hub-kid-text';

    var name = document.createElement('p');
    name.className = 'hub-kid-name';
    name.setAttribute('data-role', 'kid-name');
    name.textContent = child.name;

    var meta = document.createElement('p');
    meta.className = 'hub-kid-meta';
    meta.setAttribute('data-role', 'kid-meta');
    meta.textContent = kidMetaText(child);

    text.appendChild(name);
    text.appendChild(meta);
    main.appendChild(avatar);
    main.appendChild(text);
    li.appendChild(main);

    var actions = document.createElement('div');
    actions.className = 'hub-kid-actions';
    [['edit-kid', 'Edit', ''], ['remove-kid', 'Remove', ' hub-kid-action--danger']].forEach(function (spec) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hub-kid-action' + spec[2];
      btn.setAttribute('data-action', spec[0]);
      btn.setAttribute('aria-label', spec[1] + ' ' + child.name);
      btn.textContent = spec[1];
      actions.appendChild(btn);
    });
    li.appendChild(actions);

    return li;
  }

  function openKidModal(row) {
    var title = document.getElementById('hub-kid-modal-title');
    var idField = document.getElementById('hub-kid-id');
    if (!idField) return;

    showModalMessage('hub-kid-message', '', false);

    if (row) {
      idField.value = row.getAttribute('data-child-id') || '';
      ['name', 'school', 'grade', 'teacher', 'bedtime'].forEach(function (field) {
        var el = document.getElementById('hub-kid-' + field);
        if (el) el.value = row.getAttribute('data-' + field) || '';
      });
      if (title) title.textContent = 'Edit child';
    } else {
      idField.value = '';
      ['hub-kid-name', 'hub-kid-school', 'hub-kid-grade', 'hub-kid-teacher', 'hub-kid-bedtime'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.value = '';
      });
      if (title) title.textContent = 'Add a child';
    }

    openModal(kidOverlayId);
    var nameInput = document.getElementById('hub-kid-name');
    if (nameInput) nameInput.focus();
  }

  var kidsAdd = document.getElementById('hub-kids-add');
  if (kidsAdd) kidsAdd.addEventListener('click', function () { openKidModal(null); });

  var kidsClose = document.getElementById('hub-kid-close');
  var kidsCancel = document.getElementById('hub-kid-cancel');
  if (kidsClose) kidsClose.addEventListener('click', function () { closeModal(kidOverlayId); });
  if (kidsCancel) kidsCancel.addEventListener('click', function () { closeModal(kidOverlayId); });

  var kidsList = document.getElementById('hub-kids-list');
  if (kidsList) {
    kidsList.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var row = btn.closest('[data-role="kid-row"]');
      if (!row) return;
      var childId = row.getAttribute('data-child-id');

      if (btn.getAttribute('data-action') === 'edit-kid') {
        openKidModal(row);
        return;
      }

      if (btn.getAttribute('data-action') === 'remove-kid') {
        var name = row.querySelector('[data-role="kid-name"]').textContent.trim();
        // Removing a child hides their lunch history rather than deleting it, so this is recoverable by
        // support but not by the parent. Worth one confirm.
        if (!window.confirm('Remove ' + name + ' from the family? Their lunch history is kept but hidden.')) return;

        setBusy(btn, true);
        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/children/' + encodeURIComponent(childId), {
          method: 'DELETE',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              toast.success(name + ' removed.');
              trackEvent('hub_child_removed', {});
              row.remove();
              syncKidsEmptyState();
            } else {
              toast.error('Could not remove ' + name + '.');
              setBusy(btn, false);
            }
          })
          .catch(function () {
            toast.error('Network error.');
            setBusy(btn, false);
          });
      }
    });
  }

  var kidForm = document.getElementById('hub-kid-form');
  if (kidForm) {
    kidForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var childId = document.getElementById('hub-kid-id').value;
      var name = document.getElementById('hub-kid-name').value.trim();
      if (!name) {
        showModalMessage('hub-kid-message', 'A name is required.', true);
        return;
      }

      var body = {
        name: name,
        school: document.getElementById('hub-kid-school').value.trim(),
        grade: document.getElementById('hub-kid-grade').value.trim(),
        teacher: document.getElementById('hub-kid-teacher').value.trim(),
        bedtime: document.getElementById('hub-kid-bedtime').value.trim()
      };

      var submitBtn = document.getElementById('hub-kid-submit');
      setBusy(submitBtn, true);

      var isUpdate = !!childId;
      var url = '/api/workspace/' + encodeURIComponent(workspaceId) + '/children' + (isUpdate ? '/' + encodeURIComponent(childId) : '');

      fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok && r.data.child) {
            toast.success(isUpdate ? 'Details updated.' : name + ' added.');
            trackEvent(isUpdate ? 'hub_child_updated' : 'hub_child_added', {});
            closeModal(kidOverlayId);

            var list = document.getElementById('hub-kids-list');
            if (list) {
              var fresh = buildKidRow(r.data.child);
              var existing = list.querySelector('[data-child-id="' + r.data.child.childId + '"]');
              if (existing) {
                existing.replaceWith(fresh);
              } else {
                list.appendChild(fresh);
              }
            }
            syncKidsEmptyState();
          } else {
            showModalMessage('hub-kid-message', (r.data && r.data.error) || 'Could not save.', true);
          }
          setBusy(submitBtn, false);
        })
        .catch(function () {
          showModalMessage('hub-kid-message', 'Network error.', true);
          setBusy(submitBtn, false);
        });
    });
  }

  ['hub-emergency-add'].forEach(function (id) {
    var btn = document.getElementById(id);
    if (btn) btn.addEventListener('click', function () { openEmergencyModal(null); });
  });

  var emergencyClose = document.getElementById('hub-emergency-close');
  var emergencyCancel = document.getElementById('hub-emergency-cancel');
  if (emergencyClose) emergencyClose.addEventListener('click', function () { closeModal(emergencyOverlayId); });
  if (emergencyCancel) emergencyCancel.addEventListener('click', function () { closeModal(emergencyOverlayId); });

  var emergencyForm = document.getElementById('hub-emergency-form');
  if (emergencyForm) {
    emergencyForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var itemId = document.getElementById('hub-emergency-item-id').value;
      var name = document.getElementById('hub-emergency-name').value.trim();
      if (!name) {
        showModalMessage('hub-emergency-message', 'A name or detail is required.', true);
        return;
      }

      var body = {
        kind: document.getElementById('hub-emergency-kind').value,
        name: name,
        label: document.getElementById('hub-emergency-label').value.trim(),
        phone: document.getElementById('hub-emergency-phone').value.trim(),
        address: document.getElementById('hub-emergency-address').value.trim(),
        note: document.getElementById('hub-emergency-note').value.trim(),
        sortOrder: 0
      };

      var submitBtn = document.getElementById('hub-emergency-submit');
      setBusy(submitBtn, true);

      var isUpdate = !!itemId;
      var url = '/api/workspace/' + encodeURIComponent(workspaceId) + '/emergency' + (isUpdate ? '/' + encodeURIComponent(itemId) : '');

      fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok && r.data.item) {
            toast.success(isUpdate ? 'Emergency entry updated.' : 'Emergency entry added.');
            trackEvent(isUpdate ? 'hub_emergency_updated' : 'hub_emergency_added', { kind: body.kind });
            closeModal(emergencyOverlayId);

            var grid = document.getElementById('hub-emergency-grid');
            if (grid) {
              var existing = grid.querySelector('.hub-tile[data-item-id="' + r.data.item.itemId + '"]');
              if (existing) existing.remove();
              // The slot this filled is no longer empty. Placeholders carry no item id, so the line above
              // cannot match one.
              var slot = grid.querySelector('.hub-tile--empty[data-kind="' + r.data.item.kind + '"]');
              if (slot) slot.remove();
              // Urgency ordering is the server's call, so re-sort against the same ranks it used.
              insertTileSorted(grid, buildEmergencyTile(r.data.item), r.data.item);
            }
            syncEmergencyEmptyState();
          } else {
            showModalMessage('hub-emergency-message', (r.data && r.data.error) || 'Could not save.', true);
          }
          setBusy(submitBtn, false);
        })
        .catch(function () {
          showModalMessage('hub-emergency-message', 'Network error.', true);
          setBusy(submitBtn, false);
        });
    });
  }

  // Mirrors FamilyHubEmergencyKind.DefaultRank on the server. Only used to place a tile the user
  // just saved; a reload re-renders from the server's own ordering.
  var KIND_RANK = {
    CallFirst: 10, MedicalAlert: 20, HomeAddress: 30, EmergencyRoom: 40, UrgentCare: 50,
    Pediatrician: 60, PoisonControl: 70, Fire: 80, Police: 90, Custom: 100
  };

  function rankOf(kind, sortOrder) {
    if (sortOrder > 0) return sortOrder;
    return KIND_RANK[kind] || KIND_RANK.Custom;
  }

  function insertTileSorted(grid, tile, item) {
    var rank = rankOf(item.kind, item.sortOrder);
    var placed = false;
    Array.prototype.slice.call(grid.querySelectorAll('.hub-tile')).some(function (existing) {
      var existingRank = rankOf(existing.dataset.kind, parseInt(existing.dataset.sortOrder, 10) || 0);
      if (existingRank > rank) {
        grid.insertBefore(tile, existing);
        placed = true;
        return true;
      }
      return false;
    });
    if (!placed) grid.appendChild(tile);
  }

  var emergencyDeleteBtn = document.getElementById('hub-emergency-delete');
  if (emergencyDeleteBtn) {
    emergencyDeleteBtn.addEventListener('click', function () {
      var itemId = document.getElementById('hub-emergency-item-id').value;
      if (!itemId) return;

      confirmAction({
        title: 'Remove this emergency entry?',
        description: 'It disappears from the hub and from anything you have shared.',
        confirmText: 'Remove',
        destructive: true
      }).then(function (confirmed) {
        if (!confirmed) return;

        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/emergency/' + encodeURIComponent(itemId), {
          method: 'DELETE',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              toast.success('Removed.');
              closeModal(emergencyOverlayId);
              var grid = document.getElementById('hub-emergency-grid');
              var tile = grid ? grid.querySelector('.hub-tile[data-item-id="' + itemId + '"]') : null;
              var removedKind = tile ? tile.getAttribute('data-kind') : null;
              if (tile) tile.remove();
              restoreEmptySlot(grid, removedKind);
              syncEmergencyEmptyState();
            } else {
              showModalMessage('hub-emergency-message', 'Could not remove.', true);
            }
          })
          .catch(function () { showModalMessage('hub-emergency-message', 'Network error.', true); });
      });
    });
  }

  // ── Caregiver share ──
  function renderShare(share) {
    var live = document.getElementById('hub-share-live');
    var empty = document.getElementById('hub-share-empty');
    if (!live || !empty) return;

    if (!share || !share.isLive) {
      live.style.display = 'none';
      empty.style.display = '';
      return;
    }

    live.style.display = '';
    empty.style.display = 'none';

    var url = document.getElementById('hub-share-url');
    if (url) url.textContent = shareHost + '/hub/' + share.slug;

    var pin = document.getElementById('hub-share-pin');
    if (pin) pin.textContent = share.pin || '——————';

    var days = document.getElementById('hub-share-days');
    if (days) days.textContent = share.daysRemaining + (share.daysRemaining === 1 ? ' day' : ' days');

    var date = document.getElementById('hub-share-date');
    if (date) date.textContent = new Date(share.expiresUtc).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    var stats = document.getElementById('hub-share-stats');
    if (stats) {
      stats.style.display = share.accessCount > 0 ? '' : 'none';
      stats.textContent = 'Opened ' + share.accessCount + (share.accessCount === 1 ? ' time' : ' times');
    }
  }

  var shareCreateBtn = document.getElementById('hub-share-create');
  var shareRegenBtn = document.getElementById('hub-share-regen');
  var shareRevokeBtn = document.getElementById('hub-share-revoke');
  var shareCopyBtn = document.getElementById('hub-share-copy');
  var shareExpirySelect = document.getElementById('hub-share-expiry');

  function createOrRotateShare(btn, expiryDays, isRotate) {
    setBusy(btn, true);
    fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/caregiver-link', {
      method: 'POST',
      headers: csrfHeaders(),
      body: JSON.stringify({ expiryDays: expiryDays || 0 })
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (r.ok) {
          renderShare(r.data.share);
          toast.success(isRotate ? 'New link and PIN created — the old one stopped working.' : 'Caregiver link created.');
          trackEvent(isRotate ? 'hub_share_rotated' : 'hub_share_created', {});
        } else {
          toast.error((r.data && r.data.error) || 'Could not create the link.');
        }
        setBusy(btn, false);
      })
      .catch(function () { toast.error('Network error.'); setBusy(btn, false); });
  }

  if (shareCreateBtn) {
    shareCreateBtn.addEventListener('click', function () { createOrRotateShare(shareCreateBtn, 7, false); });
  }

  if (shareRegenBtn) {
    shareRegenBtn.addEventListener('click', function () {
      confirmAction({
        title: 'Generate a new link and PIN?',
        description: 'The link you shared before will stop working straight away. Anyone still using it will need the new one.',
        confirmText: 'Generate',
        destructive: false
      }).then(function (confirmed) {
        if (!confirmed) return;
        // 0 lets the server apply its recommended default rather than sending NaN.
        var days = shareExpirySelect ? (parseInt(shareExpirySelect.value, 10) || 0) : 0;
        createOrRotateShare(shareRegenBtn, days, true);
      });
    });
  }

  if (shareRevokeBtn) {
    shareRevokeBtn.addEventListener('click', function () {
      confirmAction({
        title: 'Turn off the caregiver link?',
        description: 'The link stops working immediately. You can create a new one whenever you need it.',
        confirmText: 'Turn off',
        destructive: true
      }).then(function (confirmed) {
        if (!confirmed) return;
        setBusy(shareRevokeBtn, true);
        fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/caregiver-link', {
          method: 'DELETE',
          headers: csrfHeaders()
        })
          .then(function (res) {
            if (res.ok) {
              renderShare(null);
              toast.success('Caregiver link turned off.');
              trackEvent('hub_share_revoked', {});
            } else {
              toast.error('Could not turn off the link.');
            }
            setBusy(shareRevokeBtn, false);
          })
          .catch(function () { toast.error('Network error.'); setBusy(shareRevokeBtn, false); });
      });
    });
  }

  if (shareCopyBtn) {
    shareCopyBtn.addEventListener('click', function () {
      var url = document.getElementById('hub-share-url');
      var pin = document.getElementById('hub-share-pin');
      if (!url) return;
      // The link is useless without the PIN, so copying hands over both in one paste.
      var text = url.textContent + (pin && pin.textContent ? '\nPIN: ' + pin.textContent : '');
      copyText(text).then(function (ok) {
        if (ok) {
          shareCopyBtn.textContent = 'Copied';
          setTimeout(function () { shareCopyBtn.textContent = 'Copy'; }, 1500);
        } else {
          toast.error('Could not copy.');
        }
      });
    });
  }

  if (shareExpirySelect) {
    shareExpirySelect.addEventListener('change', function () {
      var days = parseInt(shareExpirySelect.value, 10);
      if (!days) return; // the "Change…" placeholder

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/caregiver-link/expiry', {
        method: 'PUT',
        headers: csrfHeaders(),
        body: JSON.stringify({ expiryDays: days })
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            renderShare(r.data.share);
            toast.success('Expiry updated.');
          } else {
            toast.error((r.data && r.data.error) || 'Could not update the expiry.');
          }
          // Back to the placeholder either way — the new expiry is shown as text beside it.
          shareExpirySelect.value = '';
        })
        .catch(function () {
          toast.error('Network error.');
          shareExpirySelect.value = '';
        });
    });
  }

  // ── Download menu ──
  var dlToggle = document.getElementById('hub-dl-toggle');
  var dlMenu = document.getElementById('hub-dl-menu');

  // Relocated to <body> for the same reason admin modals are (see CLAUDE.md): left in
  // place it is trapped in .hub-header's stacking context — the app gives both
  // .hub-header and .hub-search `position: relative; z-index: 1`, so the search box,
  // being the later sibling, painted over the open menu no matter how high the menu's
  // own z-index went. It keeps the `hub` class because the --hub-* design tokens are
  // declared on that class and would otherwise stop resolving out here.
  if (dlMenu && dlMenu.parentElement !== document.body) {
    dlMenu.classList.add('hub');
    document.body.appendChild(dlMenu);
  }

  function closeDownloadMenu() {
    if (!dlMenu || !dlToggle) return;
    dlMenu.hidden = true;
    dlToggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Anchors the fixed-position menu under its toggle.
   *
   * The menu is position: fixed because on narrow screens its ancestor is a
   * horizontal scroller, which clips absolutely positioned children. Fixed escapes
   * that, but then nothing positions it, so it is done here — right-aligned to the
   * toggle and clamped so it can never hang off either edge of a phone.
   */
  function positionDownloadMenu() {
    if (!dlMenu || !dlToggle || dlMenu.hidden) return;

    var GUTTER = 8;
    var anchor = dlToggle.getBoundingClientRect();
    var menuWidth = dlMenu.offsetWidth;
    var maxLeft = document.documentElement.clientWidth - menuWidth - GUTTER;

    var left = Math.min(Math.max(GUTTER, anchor.right - menuWidth), Math.max(GUTTER, maxLeft));
    dlMenu.style.left = Math.round(left) + 'px';
    dlMenu.style.top = Math.round(anchor.bottom + 6) + 'px';
  }

  if (dlToggle && dlMenu) {
    dlToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      var willOpen = dlMenu.hidden;
      dlMenu.hidden = !willOpen;
      dlToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) positionDownloadMenu();
    });

    document.addEventListener('click', function (e) {
      if (!dlMenu.hidden && !e.target.closest('.hub-dl-wrap') && !dlMenu.contains(e.target)) closeDownloadMenu();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDownloadMenu();
    });

    // A fixed menu does not travel with its anchor, so close rather than let it drift.
    // Capture phase catches scrolling inside the action strip as well as the page.
    document.addEventListener('scroll', closeDownloadMenu, true);
    window.addEventListener('resize', closeDownloadMenu);
  }

  var dlPrint = document.getElementById('hub-dl-print');
  if (dlPrint) {
    dlPrint.addEventListener('click', function () {
      closeDownloadMenu();
      window.print();
    });
  }

  // ── Caregiver preview (local, for hubs without a live link) ──
  var carePreviewBtn = document.getElementById('hub-btn-care-preview');
  var careExitBtn = document.getElementById('hub-care-exit');
  var careBanner = document.getElementById('hub-care-banner');

  function setCarePreview(on) {
    document.body.classList.toggle('hub-care-preview', on);
    if (careBanner) careBanner.hidden = !on;
    if (on) window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (carePreviewBtn) carePreviewBtn.addEventListener('click', function () { setCarePreview(true); });
  if (careExitBtn) careExitBtn.addEventListener('click', function () { setCarePreview(false); });

  // ── Sheet drag-to-dismiss ──
  // On phones the form modals render as bottom sheets, and a sheet that cannot be
  // flicked away feels nailed down. Dragging starts only from the handle / header
  // zone, so it can never fight the body's own scrolling.
  var sheetMedia = window.matchMedia ? window.matchMedia('(max-width: 640px)') : null;

  function wireSheetDrag(dialog, id) {
    var modal = dialog.querySelector('.ws-modal');
    var header = modal && modal.querySelector('.ws-modal-header');
    if (!modal || !header) return;

    var startY = null;
    var delta = 0;
    var dragging = false;

    modal.addEventListener('touchstart', function (e) {
      if (!sheetMedia || !sheetMedia.matches) return;
      if (e.touches.length !== 1) return;
      // Handle strip + header only — a touch below the header is the form's business.
      if (e.touches[0].clientY > header.getBoundingClientRect().bottom) return;
      startY = e.touches[0].clientY;
      delta = 0;
      dragging = true;
      modal.style.transition = 'none';
    }, { passive: true });

    modal.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      delta = Math.max(0, e.touches[0].clientY - startY);
      modal.style.transform = delta > 0 ? 'translateY(' + delta + 'px)' : '';
    }, { passive: true });

    function endDrag() {
      if (!dragging) return;
      dragging = false;

      if (delta > 96) {
        // Continue the gesture: glide out from wherever the finger left it. The
        // stylesheet's closing animation would snap back to 0 first (animations
        // beat inline transforms), so it is suppressed for this close.
        modal.style.animation = 'none';
        modal.style.transition = 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)';
        modal.style.transform = 'translateY(110%)';
        closeModal(id);
        setTimeout(function () {
          modal.style.animation = '';
          modal.style.transition = '';
          modal.style.transform = '';
        }, 420);
      } else if (delta > 0) {
        modal.style.transition = 'transform 0.18s ease';
        modal.style.transform = 'translateY(0)';
        setTimeout(function () {
          modal.style.transition = '';
          modal.style.transform = '';
        }, 200);
      } else {
        modal.style.transition = '';
        modal.style.transform = '';
      }
    }

    modal.addEventListener('touchend', endDrag);
    modal.addEventListener('touchcancel', endDrag);
  }

  // ── Escape closes modals (via native cancel event) ──
  [detailOverlayId, 'ws-invite-overlay', 'ws-rename-overlay', 'ws-wiki-overlay', 'ws-cat-overlay',
   'hub-emergency-overlay', 'hub-kid-overlay', 'hub-cat-edit-overlay'].forEach(function (id) {
    var dialog = document.getElementById(id);
    if (!dialog) return;
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeModal(id);
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closeModal(id);
    });
    wireSheetDrag(dialog, id);
  });

  // ── Section management ──
  var catOverlayId = 'ws-cat-overlay';
  var catEditOverlayId = 'hub-cat-edit-overlay';
  var manageCatBtn = document.getElementById('ws-btn-manage-categories');
  var catClose = document.getElementById('ws-cat-close');
  var selectedCatColor = 'blue';
  var editingCategoryName = null;
  var editSelectedColor = 'blue';

  function renderCategoryList() {
    var list = document.getElementById('ws-cat-list');
    if (!list) return;
    list.innerHTML = '';
    if (categories.length === 0) {
      list.innerHTML = '<p class="ws-cat-empty">No sections yet — add one below.</p>';
      return;
    }
    categories.forEach(function (cat) {
      var item = document.createElement('div');
      item.className = 'ws-cat-item';
      item.innerHTML =
        '<span class="ws-cat-item-icon" aria-hidden="true">' + escapeHtml(cat.icon || '') + '</span>' +
        '<span class="ws-wiki-category" data-color="' + escapeHtml(cat.color) + '">' + escapeHtml(cat.name) + '</span>' +
        (cat.hiddenFromCaregivers ? '<span class="hub-private-chip">Private</span>' : '') +
        '<button type="button" class="ws-cat-remove-btn" data-name="' + escapeHtml(cat.name) + '" aria-label="Remove ' + escapeHtml(cat.name) + '" title="Remove"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>';
      list.appendChild(item);
    });

    list.querySelectorAll('.ws-cat-remove-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var catName = btn.dataset.name;
        confirmAction({
          title: 'Remove "' + catName + '"?',
          description: 'The section disappears from your hub. Any entries still in it must be moved first.',
          confirmText: 'Remove',
          destructive: true
        }).then(function (confirmed) {
          if (!confirmed) return;
          fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/categories/' + encodeURIComponent(catName), {
            method: 'DELETE',
            headers: csrfHeaders()
          })
            .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
            .then(function (r) {
              if (r.ok) {
                categories = r.data.categories;
                toast.success('Section removed.');
                trackEvent('workspace_category_removed', { category: catName });
                renderCategoryList();
                refreshCategorySelects();
                var card = findCardByCategory(catName);
                if (card) card.remove();
              } else {
                toast.error(r.data.error || 'Could not remove the section.');
              }
            })
            .catch(function () { toast.error('Network error.'); });
        });
      });
    });
  }

  function refreshCategorySelects() {
    var select = document.getElementById('ws-wiki-category-select');
    if (!select) return;
    var currentVal = select.value;
    select.innerHTML = '';
    categories.forEach(function (cat) {
      var opt = document.createElement('option');
      opt.value = cat.name;
      opt.textContent = cat.name;
      select.appendChild(opt);
    });
    var stillValid = Array.prototype.slice.call(select.options).some(function (o) { return o.value === currentVal; });
    select.value = stillValid ? currentVal : (categories.length > 0 ? categories[0].name : '');
  }

  // A new section has no card on the page yet; build one so the user sees the result immediately.
  function appendCategoryCard(cat) {
    var grid = document.getElementById('hub-grid');
    if (!grid || findCardByCategory(cat.name)) return;

    var card = document.createElement('article');
    card.className = 'hub-card hub-card--entering';
    card.dataset.category = cat.name;
    card.dataset.color = cat.color;
    card.style.setProperty('--care-order', String(cat.caregiverOrder > 0 ? cat.caregiverOrder : 999));

    card.innerHTML =
      '<div class="hub-card-head">' +
        '<div class="hub-card-title-wrap">' +
          '<span class="hub-card-icon" aria-hidden="true">' + escapeHtml(cat.icon || '') + '</span>' +
          '<h2 class="hub-card-title">' + escapeHtml(cat.name) + '</h2>' +
        '</div>' +
        '<div class="hub-card-head-actions">' +
          (cat.hiddenFromCaregivers ? '<span class="hub-private-chip">Private</span>' : '') +
          (isOwnerOrAdmin ? '<button type="button" class="hub-card-pencil" data-action="edit-category" data-category="' + escapeHtml(cat.name) + '" aria-label="Edit ' + escapeHtml(cat.name) + ' section" title="Edit section"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>' : '') +
        '</div>' +
      '</div>' +
      '<p class="hub-card-meta" data-role="card-meta">Nothing here yet</p>' +
      '<div class="hub-rows" data-role="rows"></div>' +
      '<p class="hub-card-blank" data-role="card-blank">Nothing here yet.</p>' +
      (canEdit ? '<button type="button" class="hub-card-add" data-action="add-entry" data-category="' + escapeHtml(cat.name) + '">+ Add entry</button>' : '');

    grid.appendChild(card);
    card.addEventListener('animationend', function () { card.classList.remove('hub-card--entering'); }, { once: true });
  }

  function openCategoryEditor(name) {
    var cat = findCategory(name);
    if (!cat) return;

    editingCategoryName = cat.name;
    editSelectedColor = cat.color;

    var original = document.getElementById('hub-cat-edit-original');
    var nameField = document.getElementById('hub-cat-edit-name');
    var iconField = document.getElementById('hub-cat-edit-icon');
    var privateField = document.getElementById('hub-cat-edit-private');
    if (original) original.value = cat.name;
    if (nameField) nameField.value = cat.name;
    if (iconField) iconField.value = cat.icon || '';
    if (privateField) privateField.checked = !!cat.hiddenFromCaregivers;

    var colors = document.getElementById('hub-cat-edit-colors');
    if (colors) {
      colors.querySelectorAll('.ws-cat-color-btn').forEach(function (b) {
        b.classList.toggle('ws-cat-color-btn--selected', b.dataset.color === cat.color);
        b.setAttribute('aria-pressed', b.dataset.color === cat.color ? 'true' : 'false');
      });
    }

    openModal(catEditOverlayId);
  }

  var catEditColors = document.getElementById('hub-cat-edit-colors');
  if (catEditColors) {
    catEditColors.querySelectorAll('.ws-cat-color-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        catEditColors.querySelectorAll('.ws-cat-color-btn').forEach(function (b) { b.classList.remove('ws-cat-color-btn--selected'); b.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('ws-cat-color-btn--selected');
        btn.setAttribute('aria-pressed', 'true');
        editSelectedColor = btn.dataset.color;
      });
    });
  }

  var catEditClose = document.getElementById('hub-cat-edit-close');
  var catEditCancel = document.getElementById('hub-cat-edit-cancel');
  if (catEditClose) catEditClose.addEventListener('click', function () { closeModal(catEditOverlayId); });
  if (catEditCancel) catEditCancel.addEventListener('click', function () { closeModal(catEditOverlayId); });

  var catEditForm = document.getElementById('hub-cat-edit-form');
  if (catEditForm) {
    catEditForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!editingCategoryName) return;

      var newName = document.getElementById('hub-cat-edit-name').value.trim();
      if (!newName) {
        showModalMessage('hub-cat-edit-message', 'A section needs a name.', true);
        return;
      }

      var submitBtn = document.getElementById('hub-cat-edit-submit');
      setBusy(submitBtn, true);

      var body = {
        name: newName,
        color: editSelectedColor,
        // Empty string (not null) so the server clears a custom icon back to the derived default.
        icon: document.getElementById('hub-cat-edit-icon').value.trim(),
        hiddenFromCaregivers: document.getElementById('hub-cat-edit-private').checked
      };

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/categories/' + encodeURIComponent(editingCategoryName), {
        method: 'PUT',
        headers: csrfHeaders(),
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            categories = r.data.categories;
            toast.success('Section updated.');
            trackEvent('workspace_category_updated', {});
            closeModal(catEditOverlayId);
            // A rename rewrites the category on every entry server-side; reloading is the simplest
            // way to be certain the page matches what was actually stored.
            window.location.reload();
          } else {
            showModalMessage('hub-cat-edit-message', r.data.error || 'Could not update the section.', true);
            setBusy(submitBtn, false);
          }
        })
        .catch(function () {
          showModalMessage('hub-cat-edit-message', 'Network error.', true);
          setBusy(submitBtn, false);
        });
    });
  }

  var colorPicker = document.getElementById('ws-cat-color-picker');
  if (colorPicker) {
    colorPicker.querySelectorAll('.ws-cat-color-btn').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.color === selectedCatColor ? 'true' : 'false');
      if (btn.dataset.color === selectedCatColor) btn.classList.add('ws-cat-color-btn--selected');
      btn.addEventListener('click', function () {
        colorPicker.querySelectorAll('.ws-cat-color-btn').forEach(function (b) { b.classList.remove('ws-cat-color-btn--selected'); b.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('ws-cat-color-btn--selected');
        btn.setAttribute('aria-pressed', 'true');
        selectedCatColor = btn.dataset.color;
      });
    });
  }

  if (manageCatBtn) {
    manageCatBtn.addEventListener('click', function () {
      renderCategoryList();
      openModal(catOverlayId);
    });
  }

  if (catClose) catClose.addEventListener('click', function () { closeModal(catOverlayId); });

  var catAddBtn = document.getElementById('ws-cat-add-btn');
  if (catAddBtn) {
    catAddBtn.addEventListener('click', function () {
      var nameInput = document.getElementById('ws-cat-name-input');
      var iconInput = document.getElementById('ws-cat-icon-input');
      var privateInput = document.getElementById('ws-cat-private-input');
      var name = nameInput ? nameInput.value.trim() : '';
      if (!name) {
        showModalMessage('ws-cat-message', 'Give the section a name.', true);
        return;
      }

      setBusy(catAddBtn, true);

      fetch('/api/workspace/' + encodeURIComponent(workspaceId) + '/categories', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          name: name,
          color: selectedCatColor,
          icon: iconInput ? iconInput.value.trim() : '',
          hiddenFromCaregivers: privateInput ? privateInput.checked : false
        })
      })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
        .then(function (r) {
          if (r.ok) {
            categories = r.data.categories;
            toast.success('Section "' + name + '" added.');
            trackEvent('workspace_category_added', { category: name, color: selectedCatColor });
            if (nameInput) nameInput.value = '';
            if (iconInput) iconInput.value = '';
            if (privateInput) privateInput.checked = false;
            var msgEl = document.getElementById('ws-cat-message');
            if (msgEl) msgEl.hidden = true;
            renderCategoryList();
            refreshCategorySelects();
            var added = findCategory(name);
            if (added) appendCategoryCard(added);
          } else {
            showModalMessage('ws-cat-message', r.data.error || 'Could not add the section.', true);
          }
          setBusy(catAddBtn, false);
        })
        .catch(function () {
          showModalMessage('ws-cat-message', 'Network error.', true);
          setBusy(catAddBtn, false);
        });
    });
  }

  // ── SignalR real-time ──
  if (workspaceId) {
    var wsConnection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/workspace')
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    function joinWorkspaceChannel() {
      return wsConnection.invoke('JoinWorkspace', workspaceId);
    }

    wsConnection.on('MemberJoined', function (payload) {
      toast.success((payload.displayName || 'A new member') + ' joined the ' + wsLabel.toLowerCase() + '.');
      trackEvent('workspace_member_joined_realtime', {});
      var memberList = document.getElementById('ws-member-list');
      if (memberList && payload.userId !== currentUserId) {
        var card = buildMemberCard(payload);
        memberList.appendChild(card);
        updateMemberCount(1);
      }
    });

    wsConnection.on('MemberLeft', function (payload) {
      toast.info((payload.displayName || 'A member') + ' left the ' + wsLabel.toLowerCase() + '.');
      if (payload.userId && payload.userId !== currentUserId) {
        var card = document.querySelector('.ws-side-member[data-user-id="' + payload.userId + '"]') || document.querySelector('.ws-member-tile[data-user-id="' + payload.userId + '"]') || document.querySelector('.ws-member-card[data-user-id="' + payload.userId + '"]');
        animateCardRemoval(card, function () { updateMemberCount(-1); });
      }
    });

    wsConnection.on('MemberRemoved', function (payload) {
      if (payload.userId === currentUserId) {
        toast.info('You have been removed from this ' + wsLabel.toLowerCase() + '.');
        setTimeout(function () { window.location.reload(); }, 1500);
      }
    });

    wsConnection.on('IntelligenceUpdated', function (payload) {
      if (payload && payload.userId !== currentUserId) {
        var banner = document.getElementById('ws-wiki-refresh');
        if (banner) banner.classList.remove('hidden');
      }
    });

    wsConnection.on('ActivityPosted', function (payload) {
      if (!payload || !payload.description) return;
      var activityList = document.getElementById('ws-activity-list');
      if (!activityList) {
        var panel = document.getElementById('ws-panel-activity');
        if (!panel) return;
        var emptyState = panel.querySelector('.ws-empty');
        if (emptyState) emptyState.remove();
        activityList = document.createElement('div');
        activityList.className = 'ws-activity-list';
        activityList.id = 'ws-activity-list';
        panel.appendChild(activityList);
      }
      var item = document.createElement('div');
      item.className = 'ws-activity-item ws-activity-item--entering';
      item.innerHTML = '<div class="ws-activity-dot" aria-hidden="true"></div>' +
        '<div class="ws-activity-content">' +
        '<p class="ws-activity-desc">' + escapeHtml(payload.description) + '</p>' +
        '<span class="ws-activity-time">Just now</span></div>';
      activityList.insertBefore(item, activityList.firstChild);
    });

    wsConnection.onreconnected(function () {
      return joinWorkspaceChannel()
        .catch(function (err) {
          console.warn('Workspace SignalR group rejoin failed:', err);
        });
    });

    wsConnection.start()
      .then(function () {
        return joinWorkspaceChannel();
      })
      .catch(function (err) {
        console.warn('Workspace SignalR connection failed:', err);
      });
  }

  var refreshBanner = document.getElementById('ws-wiki-refresh');
  if (refreshBanner) {
    refreshBanner.addEventListener('click', function () {
      window.location.reload();
    });
  }

  refreshAllCards();
  syncEmergencyEmptyState();
}

// Init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWorkspacePage);
} else {
  initWorkspacePage();
}
